import { Chart as G2Chart } from '@antv/g2';
import { forwardRef, HTMLAttributes, Ref, useEffect, useImperativeHandle, useRef } from 'react';

import { IBaseChartProps, IComponent } from '../../../models/components.models';
import { useClassNames } from '../../../utils/hooks';
import { IPieChartClassNames, IPieChartStyles, pieChartStylesDefault } from './PieChart.styles';

const render = (el: HTMLElement, data: { [key: string]: any }[], labelFieldName: string, valueFieldNames: string[]) => {
  const chart = new G2Chart({ container: el, autoFit: true });
  chart.coordinate({ type: 'theta', innerRadius: 0.6 });
  chart
    .interval()
    .transform({ type: 'stackY' })
    .data(data)
    .encode('y', valueFieldNames[0])
    .encode('color', labelFieldName)
    .style({
      stroke: 'white',
      inset: 1,
      radius: 10,
    })
    .scale('color', { palette: 'spectral', offset: (t: number) => t * 0.8 + 0.1 })
    .label({ text: labelFieldName, fontSize: 10, fontWeight: 'bold', fill: 'black' })
    .label({ text: valueFieldNames[0], fontSize: 9, fill: 'black', dy: 12 })
    .animate('enter', { type: 'waveIn' })
    .legend(false);
  chart.render();
};

export interface IPieChartProps extends IComponent<IPieChartStyles>, HTMLAttributes<HTMLDivElement>, IBaseChartProps {}

export const PieChart = forwardRef((props: IPieChartProps, ref: Ref<HTMLDivElement>) => {
  // some variables are not used, but they need to get `elementProps`.
  const { className, styles, data = [], labelFieldName = '', valueFieldNames = [], ...elementProps } = props;
  const refRoot = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  useEffect(() => {
    if (refRoot.current) {
      render(refRoot.current, data, labelFieldName, valueFieldNames);
    }
  }, [data, labelFieldName, valueFieldNames]);

  const classNames = useClassNames<Partial<IPieChartStyles>, IPieChartClassNames>(
    'PieChart',
    pieChartStylesDefault,
    styles,
    className
  );
  return <div {...elementProps} ref={refRoot} className={classNames.root} />;
});
